import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface InfoSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
}

export const InfoSheet: React.FC<InfoSheetProps> = ({ isOpen, onOpenChange, title, children }) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-50"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: '0%' }}
                exit={{ y: '100%' }}
                transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
                className="fixed bottom-0 left-0 right-0 max-h-[60vh] px-4 pt-4 bg-black/40 backdrop-blur-xl z-50 rounded-t-2xl flex flex-col border-t border-white/10"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
              >
                {/* Drag Handle */}
                <div className="flex-shrink-0 text-center mb-2">
                  <div className="inline-block w-10 h-1.5 bg-white/40 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                  <Dialog.Title className="text-xl font-bold text-white">{title}</Dialog.Title>
                  <Dialog.Close asChild>
                    <button 
                      className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </Dialog.Close>
                </div>
                
                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-grow">
                  {children}
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};
